import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import FavoriteIcon from '@mui/icons-material/Favorite'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'

/**
 * Development Theme Preview Page
 * Displays commonly used Material UI components with different states
 * Helps preview and fix theme styling issues
 */
const DevTheme = () => {
  const [selectValue, setSelectValue] = useState('')
  const [autocompleteValue, setAutocompleteValue] = useState([])
  const [textFieldValue, setTextFieldValue] = useState('')

  const autocompleteOptions = ['Option 1', 'Option 2', 'Option 3', 'Option 4', 'Option 5']

  // Component section renderer
  const ComponentSection = ({ title, description, children }) => (
    <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>
      )}
      <Divider sx={{ mb: 3 }} />
      {children}
    </Paper>
  )

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Theme Preview & Development
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Preview commonly used Material UI components with different states to help identify and fix theme styling issues.
        </Typography>
      </Paper>

      {/* Buttons Section */}
      <ComponentSection title="Buttons" description="Different button variants and states">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Contained Buttons
            </Typography>
            <Stack spacing={2}>
              <Button variant="contained" color="primary">
                Primary Contained
              </Button>
              <Button variant="contained" color="secondary">
                Secondary Contained
              </Button>
              <Button variant="contained" disabled>
                Disabled Contained
              </Button>
              <Button variant="contained" startIcon={<AddIcon />}>
                With Icon
              </Button>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Outlined Buttons
            </Typography>
            <Stack spacing={2}>
              <Button variant="outlined" color="primary">
                Primary Outlined
              </Button>
              <Button variant="outlined" color="secondary">
                Secondary Outlined
              </Button>
              <Button variant="outlined" disabled>
                Disabled Outlined
              </Button>
              <Button variant="outlined" startIcon={<DeleteIcon />}>
                With Icon
              </Button>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Text Buttons & Sizes
            </Typography>
            <Stack spacing={2}>
              <Button variant="text" color="primary">
                Primary Text
              </Button>
              <Button variant="text" color="secondary">
                Secondary Text
              </Button>
              <Button variant="text" disabled>
                Disabled Text
              </Button>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained">
                  Small
                </Button>
                <Button size="medium" variant="contained">
                  Medium
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </ComponentSection>

      {/* Text Fields Section */}
      <ComponentSection title="Text Fields" description="Different text field states and variants">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={2}>
              <TextField label="Standard" placeholder="Enter text..." fullWidth />
              <TextField label="Filled" variant="filled" placeholder="Enter text..." fullWidth />
              <TextField label="Outlined" variant="outlined" placeholder="Enter text..." fullWidth />
              <TextField label="With Value" value={textFieldValue} onChange={(e) => setTextFieldValue(e.target.value)} fullWidth />
              <TextField label="Disabled" disabled value="Disabled value" fullWidth />
              <TextField label="Error" error helperText="Error message" fullWidth />
              <TextField label="Required" required fullWidth />
              <TextField label="With Helper Text" helperText="Helper text message" fullWidth />
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={2}>
              <TextField label="Small Size" size="small" fullWidth />
              <TextField label="Multiline" multiline rows={4} fullWidth />
              <TextField label="Password" type="password" fullWidth />
              <TextField label="Number" type="number" fullWidth />
              <TextField label="With Icon" InputProps={{ startAdornment: <FavoriteIcon sx={{ mr: 1, color: 'text.secondary' }} /> }} fullWidth />
            </Stack>
          </Grid>
        </Grid>
      </ComponentSection>

      {/* Autocomplete Section */}
      <ComponentSection title="Autocomplete" description="Autocomplete component with different states">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel sx={{ mb: 1 }}>Single Select</FormLabel>
              <Autocomplete options={autocompleteOptions} renderInput={(params) => <TextField {...params} placeholder="Select an option" />} />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel sx={{ mb: 1 }}>Multiple Select (Empty)</FormLabel>
              <Autocomplete multiple options={autocompleteOptions} value={[]} renderInput={(params) => <TextField {...params} placeholder="Select certificates" />} />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel sx={{ mb: 1 }}>Multiple Select (With Values)</FormLabel>
              <Autocomplete
                multiple
                options={autocompleteOptions}
                value={autocompleteValue}
                onChange={(event, newValue) => setAutocompleteValue(newValue)}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select certificates"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: '2.5rem',
                        height: 'auto',
                        padding: '6px 12px',
                      },
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel sx={{ mb: 1 }}>Disabled</FormLabel>
              <Autocomplete disabled options={autocompleteOptions} value={[autocompleteOptions[0]]} renderInput={(params) => <TextField {...params} />} />
            </FormControl>
          </Grid>
        </Grid>
      </ComponentSection>

      {/* Select Section */}
      <ComponentSection title="Select" description="Select dropdown component states">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Standard Select</InputLabel>
              <Select value={selectValue} label="Standard Select" onChange={(e) => setSelectValue(e.target.value)}>
                <MenuItem value="option1">Option 1</MenuItem>
                <MenuItem value="option2">Option 2</MenuItem>
                <MenuItem value="option3">Option 3</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>With Value</InputLabel>
              <Select value={selectValue || 'option1'} label="With Value">
                <MenuItem value="option1">Option 1</MenuItem>
                <MenuItem value="option2">Option 2</MenuItem>
                <MenuItem value="option3">Option 3</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Disabled</InputLabel>
              <Select value="option1" label="Disabled" disabled>
                <MenuItem value="option1">Option 1</MenuItem>
                <MenuItem value="option2">Option 2</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }} error>
              <InputLabel>Error State</InputLabel>
              <Select value="" label="Error State">
                <MenuItem value="option1">Option 1</MenuItem>
                <MenuItem value="option2">Option 2</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </ComponentSection>

      {/* Chips Section */}
      <ComponentSection title="Chips" description="Chip component variants">
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Basic Chips
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label="Default" />
              <Chip label="Primary" color="primary" />
              <Chip label="Secondary" color="secondary" />
              <Chip label="Success" color="success" />
              <Chip label="Error" color="error" />
              <Chip label="Warning" color="warning" />
              <Chip label="Info" color="info" />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Variants
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label="Filled" variant="filled" />
              <Chip label="Outlined" variant="outlined" />
              <Chip label="Outlined Primary" variant="outlined" color="primary" />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              With Icons & Actions
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<FavoriteIcon />} label="With Icon" />
              <Chip label="Deletable" onDelete={() => {}} />
              <Chip label="Clickable" onClick={() => {}} />
              <Chip icon={<CheckCircleIcon />} label="Icon & Delete" onDelete={() => {}} />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Sizes
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label="Small" size="small" />
              <Chip label="Medium" size="medium" />
            </Stack>
          </Box>
        </Stack>
      </ComponentSection>

      {/* Form Controls Section */}
      <ComponentSection title="Form Controls" description="FormControl and FormLabel components">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15rem',
                }}
              >
                Filter by Sport Type
              </FormLabel>
              <TextField placeholder="Example input" />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15rem',
                }}
              >
                Filter by Certificate (Empty)
              </FormLabel>
              <Autocomplete multiple options={autocompleteOptions} value={[]} renderInput={(params) => <TextField {...params} placeholder="Select certificates" />} />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15rem',
                }}
              >
                Filter by Certificate (With Selected Items)
              </FormLabel>
              <Autocomplete
                multiple
                options={autocompleteOptions}
                value={['Option 1', 'Option 3']}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select certificates"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: '2.5rem',
                        height: 'auto',
                        padding: '6px 12px',
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15rem',
                }}
              >
                Select with Pre-selected Value
              </FormLabel>
              <Select value="option2" label="Select with Pre-selected Value">
                <MenuItem value="option1">Option 1</MenuItem>
                <MenuItem value="option2">Option 2</MenuItem>
                <MenuItem value="option3">Option 3</MenuItem>
                <MenuItem value="option4">Option 4</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }} error>
              <FormLabel
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15rem',
                }}
              >
                Error State
              </FormLabel>
              <TextField error helperText="This field has an error" />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }} disabled>
              <FormLabel
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15rem',
                }}
              >
                Disabled State
              </FormLabel>
              <TextField disabled value="Disabled value" />
            </FormControl>
          </Grid>
        </Grid>
      </ComponentSection>

      {/* Icon Buttons Section */}
      <ComponentSection title="Icon Buttons" description="IconButton component variants">
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <IconButton color="primary">
            <FavoriteIcon />
          </IconButton>
          <IconButton color="secondary">
            <AddIcon />
          </IconButton>
          <IconButton color="error">
            <DeleteIcon />
          </IconButton>
          <IconButton disabled>
            <CheckCircleIcon />
          </IconButton>
          <IconButton size="small">
            <FavoriteIcon />
          </IconButton>
          <IconButton size="large">
            <FavoriteIcon />
          </IconButton>
        </Stack>
      </ComponentSection>
    </Container>
  )
}

export default DevTheme
